import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Chip,
  Button,
  Typography,
  Autocomplete,
  TextField,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import apiClient from '../../../utils/axiosClient';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import DateRangeSelector from '../../../components/DateRangeSelector';
import PointhomeSettlementStatusDashboard from '../../../components/pointhome/settlementStatus/PointhomeSettlementStatusDashboard';
import type { SettlementStatusData } from '../../../types/pointhomeSettlementStatus.types';

const OS_OPTIONS = [
  { label: 'ALL', value: 'all' },
  { label: 'AOS', value: 'aos' },
  { label: 'IOS', value: 'ios' },
];

export default function PointhomeSettlementStatus() {
  const [data, setData] = useState<SettlementStatusData>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [initialized, setInitialized] = useState(false);
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [selectedOs, setSelectedOs] = useState('all');

  const fetchAppList = useCallback(() => {
    apiClient.get('/api/v1/stat/pointhome/app/list').then((res) => {
      setAppOptions(['앱 전체', ...res.data]);
    }).catch(() => {
      // 옵션 로드 실패 시에도 화면은 동작하도록 침묵 처리
    });
  }, []);

  useEffect(() => {
    fetchAppList();
  }, [fetchAppList]);

  const fetchData = useCallback(() => {
    if (!startDate || !endDate) return;

    setLoading(true);

    const params: Record<string, string> = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
    };
    if (selectedApp && selectedApp !== '앱 전체') params.app_name = selectedApp;
    if (selectedOs !== 'all') params.os_type = selectedOs;

    apiClient
      .get('/api/v1/stat/pointhome/settlement/status', { params })
      .then((res) => {
        setData(res.data);
        setErrorMessage('');
      })
      .catch(() => {
        setData([]);
        setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, selectedApp, selectedOs]);

  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (initialized && startDate && endDate) {
      fetchData();
    }
  }, [initialized, startDate, endDate, selectedApp, selectedOs, fetchData]);

  const appDisplayOptions = useMemo(
    () => appOptions.filter((o) => o !== '앱 전체'),
    [appOptions]
  );

  return (
    <Box sx={{ opacity: loading ? 0.3 : 1, transition: 'opacity 0.3s ease', minHeight: '100vh' }}>
      {/* ── 필터 영역 ── */}
      <Box display="flex" gap={2} flexWrap="wrap" alignItems="stretch">
        {/* 기간 설정 */}
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onSearch={fetchData}
        />

        {/* 앱 + OS 통합 필터 카드 */}
        <Box
          sx={{
            p: 2.5,
            borderRadius: 3,
            bgcolor: '#ffffff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            border: '1px solid #f1f5f9',
            minHeight: { xs: 'auto', md: 125 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} mb={1.5} color="#475569" sx={{ fontSize: '0.95rem' }}>
            필터 설정
          </Typography>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            {/* 앱 선택 */}
            <Autocomplete<string, false, false, false>
              options={appDisplayOptions}
              value={selectedApp === '앱 전체' ? null : selectedApp}
              onChange={(_, v) => setSelectedApp(v ?? '앱 전체')}
              isOptionEqualToValue={(opt, val) => opt === val}
              clearOnEscape
              sx={{ width: 220 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="앱 전체"
                  variant="outlined"
                  size="small"
                  sx={{
                    bgcolor: '#f8fafc',
                    borderRadius: 2.5,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#cbd5e1' },
                      '&.Mui-focused fieldset': { borderColor: '#0044D2' },
                    },
                  }}
                />
              )}
            />

            {/* OS 토글 버튼 */}
            <Box display="flex" gap={0.75}>
              {OS_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  size="small"
                  variant={selectedOs === opt.value ? 'contained' : 'outlined'}
                  onClick={() => setSelectedOs(opt.value)}
                  sx={{
                    minWidth: 52,
                    height: 36,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderRadius: 2,
                    ...(selectedOs === opt.value
                      ? { backgroundColor: '#0044D2', borderColor: '#0044D2', '&:hover': { backgroundColor: '#0033a0' } }
                      : { borderColor: '#e2e8f0', color: '#374151', '&:hover': { backgroundColor: '#f3f4f6', borderColor: '#cbd5e1' } }),
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── 콘텐츠 영역 ── */}
      <Box
        sx={PAGE_CARD_SX}
      >
        {/* 앱 Chip */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': { backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6' },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Box component="span" sx={{ ml: 2 }}>데이터 로딩 중...</Box>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : data.length > 0 && startDate && endDate ? (
          <PointhomeSettlementStatusDashboard
            data={data}
            startDate={startDate}
            endDate={endDate}
            selectedApp={selectedApp}
            selectedOs={selectedOs}
          />
        ) : null}
      </Box>
    </Box>
  );
}
